import { useCallback, useEffect, useMemo, useState } from "react";
import { Gift, Plus, Search, Check, Send, Trash2, Save, Wallet, Link2, RefreshCw, ChevronRight, Sparkles, CalendarCheck, Pencil, X } from "lucide-react";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";
import LoyaltyService from "../../../services/loyalty/LoyaltyService";
import { showError, showSuccess, showWarning, confirmDialog, notifyApiError } from "../../../components/alertas";
import { CountryPhoneSelector } from "../../../components/CountryPhoneSelector";
import 'flag-icons/css/flag-icons.min.css';
import { normalizePhone } from "../../../services/countries/countryData";
import "./lealtad.css";

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-MX", { day: "numeric", month: "short" }) : "");
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" }) : "");
const relDay = (d) => {
  if (!d) return "";
  const date = new Date(d); const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Hoy";
  if (date.toDateString() === y.toDateString()) return "Ayer";
  return fmtDate(d);
};

/** Barra de progreso del ciclo: visitas / meta máxima. */
function Progress({ visits, max, completed }) {
  const pct = max > 0 ? Math.min(100, Math.round((visits / max) * 100)) : 0;
  return (
    <div className="cp-loy-progress" aria-hidden>
      <div className="cp-loy-bar"><span style={{ width: `${completed ? 100 : pct}%` }} /></div>
      <span className="cp-loy-progress-label">{completed ? "Completado" : max > 0 ? `${visits} de ${max}` : `${visits}`}</span>
    </div>
  );
}

/** Vista previa de la tarjeta con el color de marca (misma que ve el cliente en Wallet). */
function CardPreview({ businessName, tiers, visits = 3, repeatCycle }) {
  const max = tiers.length ? tiers[tiers.length - 1].visits : 0;
  const next = tiers.find((t) => t.visits > visits);
  return (
    <div className="cp-loy-preview" aria-hidden>
      <div className="cp-loy-preview-head"><Gift size={16} /> {businessName} · Lealtad</div>
      <div className="cp-loy-preview-big">{visits} <small>/ {max || "?"} visitas</small></div>
      <div className="cp-loy-preview-sub">{next ? `${next.title} a las ${next.visits} visitas` : tiers.length ? "Programa completado" : "Agrega una recompensa"}</div>
      {repeatCycle && tiers.length > 0 && <div className="cp-loy-preview-foot">Al completar, vuelve a empezar</div>}
    </div>
  );
}

/**
 * Lealtad (add-on loyaltyCards): programa de recompensas por visitas, citas pasadas
 * por registrar, lista de tarjetas y detalle en sheet (registrar visita, canjear,
 * reenviar). Las tarjetas viven en el backend (loyalty_cards); Google Wallet es
 * opcional y se sincroniza solo.
 */
export default function LoyaltyTab({ clientData, refetchClientData, canEditProgram = true }) {
  const clientId = clientData?.id;
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const side = isDesktop ? "right" : "bottom";
  const businessName = clientData?.business?.name || "Tu negocio";

  const [summary, setSummary] = useState(null);
  const [cards, setCards] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState("");
  const [editingProgram, setEditingProgram] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const program = summary?.program;
  const enabled = !!program?.enabled;

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [s, c] = await Promise.all([LoyaltyService.getSummary(clientId), LoyaltyService.getCards(clientId, "")]);
      setSummary(s); setCards(c);
    } catch (e) {
      notifyApiError(e, "Cargar lealtad");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadPending = useCallback(async () => {
    if (!clientId) return;
    setPendingLoading(true);
    try { setPending(await LoyaltyService.getPendingVisits(clientId)); } catch { setPending([]); } finally { setPendingLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (enabled) loadPending(); else setPendingLoading(false); }, [enabled, loadPending]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase(); const digits = term.replace(/\D/g, "");
    if (!term) return cards;
    return cards.filter((c) => (c.name || "").toLowerCase().includes(term) || (digits.length >= 3 && (c.phone || "").includes(digits)));
  }, [cards, q]);

  const upsertCard = (card) => {
    setCards((prev) => (prev.some((c) => c.id === card.id) ? prev.map((c) => (c.id === card.id ? card : c)) : [card, ...prev]));
    if (selected?.id === card.id) setSelected(card);
  };
  const refreshSummary = () => LoyaltyService.getSummary(clientId).then(setSummary).catch(() => {});

  const announceVisit = (res) => {
    if (res.duplicate) { showWarning("Esa cita ya contaba", "La visita de esa cita ya estaba registrada."); return; }
    if (res.earned?.length) showSuccess(`¡${res.card.name || "El cliente"} ganó ${res.earned.map((t) => t.title).join(" y ")}!`, "Se le avisó por WhatsApp.");
    else if (res.cycleCompleted) showSuccess("Visita registrada", "Completó el ciclo y vuelve a empezar.");
    else showSuccess("Visita registrada", res.card.completed ? "Ya completó el programa." : `Lleva ${res.card.visits} de ${res.card.maxVisits}.`);
  };

  const registerVisit = async (card) => {
    setBusy(`visit:${card.id}`);
    try { const res = await LoyaltyService.registerVisit(clientId, card.id); upsertCard(res.card); announceVisit(res); refreshSummary(); }
    catch (e) { notifyApiError(e, "Registrar visita"); }
    finally { setBusy(""); }
  };

  const registerPending = async (p) => {
    setBusy(`pending:${p.eventId}`);
    try {
      const res = await LoyaltyService.registerVisitByPhone(clientId, { phone: p.telefono, name: p.nombre, eventId: p.eventId });
      upsertCard(res.card); announceVisit(res); refreshSummary();
      setPending((prev) => prev.filter((x) => x.eventId !== p.eventId));
    } catch (e) { notifyApiError(e, "Registrar visita"); }
    finally { setBusy(""); }
  };

  const redeem = async (card, reward) => {
    const ok = await confirmDialog({ title: `¿Canjear "${reward.title}"?`, text: `Se marca como usada en la tarjeta de ${card.name || card.phoneMasked}. El cliente recibe la confirmación.`, confirmButtonText: "Sí, canjear", cancelButtonText: "No" });
    if (!ok) return;
    setBusy(`redeem:${reward.id}`);
    try { upsertCard(await LoyaltyService.redeemReward(clientId, card.id, reward.id)); showSuccess("Recompensa canjeada"); refreshSummary(); }
    catch (e) { notifyApiError(e, "Canjear recompensa"); }
    finally { setBusy(""); }
  };

  const send = async (card) => {
    setBusy(`send:${card.id}`);
    try { await LoyaltyService.sendCard(clientId, card.id); showSuccess("Tarjeta enviada", "Le llegó por WhatsApp con su saldo y su link."); }
    catch (e) { notifyApiError(e, "Reenviar tarjeta"); }
    finally { setBusy(""); }
  };

  const copyLink = async (card) => {
    try { await navigator.clipboard.writeText(card.url); showSuccess("Link copiado"); } catch { showError("No se pudo copiar", card.url); }
  };

  const rotate = async (card) => {
    const ok = await confirmDialog({ title: "¿Generar un link nuevo?", text: "El link anterior deja de funcionar. Úsalo si el cliente lo compartió por error.", confirmButtonText: "Sí, generar", cancelButtonText: "No", icon: "warning" });
    if (!ok) return;
    try { upsertCard(await LoyaltyService.rotateToken(clientId, card.id)); showSuccess("Link nuevo listo", "Reenvíaselo por WhatsApp."); }
    catch (e) { notifyApiError(e, "Generar link"); }
  };

  const adjust = async (card, visits) => {
    setBusy(`adjust:${card.id}`);
    try { upsertCard(await LoyaltyService.adjustVisits(clientId, card.id, visits)); setAdjusting(false); showSuccess("Visitas corregidas"); }
    catch (e) { notifyApiError(e, "Ajustar visitas"); }
    finally { setBusy(""); }
  };

  const stats = summary?.stats || {};

  return (
    <section className="cp-loyalty">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title" data-tour="lealtad.title">Lealtad {stats.cards > 0 && <span className="cp-count">{stats.cards} {stats.cards === 1 ? "tarjeta" : "tarjetas"}</span>}</h2>
          <p className="cp-page-sub">Cada cita atendida suma una visita; a cada meta, una recompensa.</p>
        </div>
        {enabled && (
          <div className="cp-page-actions">
            <button type="button" className="cp-btn cp-btn-primary" onClick={() => setShowNew(true)} data-tour="lealtad.new"><Plus size={18} /> <span>Nueva tarjeta</span></button>
          </div>
        )}
      </header>

      <div className="cp-loy-grid">
        <div className="cp-loy-col">
          {/* Programa */}
          <article className="cp-card" data-tour="lealtad.program">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><Gift size={18} /> Programa</h3>
              {loading ? <span className="cp-skeleton" style={{ width: 70, height: 22 }} /> : <span className={`cp-pill ${enabled ? "cp-pill-ok" : ""}`}>{enabled ? "Activo" : "Apagado"}</span>}
            </div>
            {loading ? (
              <div className="cp-stack"><div className="cp-skeleton" style={{ height: 44 }} /><div className="cp-skeleton" style={{ height: 44 }} /></div>
            ) : program ? (
              <div className="cp-stack">
                {program.tiers.length > 0 ? (
                  <ul className="cp-loy-tiers">
                    {program.tiers.map((t) => (
                      <li key={t.visits}><span className="cp-loy-tier-n">{t.visits}</span><span className="cp-loy-tier-title">{t.title}{t.description && <small>{t.description}</small>}</span></li>
                    ))}
                  </ul>
                ) : (
                  <p className="cp-muted">Todavía no tienes recompensas. Define a cuántas visitas se gana cada una.</p>
                )}
                <p className="cp-hint">
                  {program.repeatCycle ? "Al completar la última meta la cuenta vuelve a cero y se puede volver a ganar." : "Cada cliente gana cada recompensa una sola vez."}
                  {program.sendOnFirstAppointment ? " La tarjeta se manda por WhatsApp con la primera cita." : ""}
                </p>
                {canEditProgram ? (
                  <button type="button" className="cp-btn" onClick={() => setEditingProgram(true)}><Pencil size={16} /> {program.tiers.length ? "Editar programa" : "Configurar programa"}</button>
                ) : (
                  <p className="cp-hint">Las metas y recompensas las configura el dueño del negocio.</p>
                )}
              </div>
            ) : (
              <p className="cp-muted">Tu plan no incluye tarjetas de lealtad. Escríbenos para activarlas.</p>
            )}
          </article>

          {/* KPIs */}
          {enabled && (
            <div className="cp-loy-kpis" data-tour="lealtad.kpis">
              <div className="cp-loy-kpi"><b>{stats.cards ?? 0}</b><span>Tarjetas</span></div>
              <div className="cp-loy-kpi"><b>{stats.visits ?? 0}</b><span>Visitas este mes</span></div>
              <div className="cp-loy-kpi"><b>{stats.pendingRewards ?? 0}</b><span>Por canjear</span></div>
              <div className="cp-loy-kpi"><b>{stats.redeemed ?? 0}</b><span>Canjes del mes</span></div>
            </div>
          )}

          {/* Citas por registrar */}
          {enabled && (
            <article className="cp-card" data-tour="lealtad.pending">
              <div className="cp-card-head">
                <h3 className="cp-card-title"><CalendarCheck size={18} /> Por registrar {pending.length > 0 && <span className="cp-count">{pending.length}</span>}</h3>
                <button type="button" className="cp-iconbtn" onClick={loadPending} aria-label="Actualizar" disabled={pendingLoading}><RefreshCw size={16} className={pendingLoading ? "cp-loy-spin" : ""} /></button>
              </div>
              {pendingLoading ? (
                <div className="cp-stack"><div className="cp-skeleton" style={{ height: 56 }} /><div className="cp-skeleton" style={{ height: 56 }} /></div>
              ) : pending.length === 0 ? (
                <p className="cp-muted">Citas de las últimas 48 horas que ya pasaron y no tienen visita registrada. Ahora no hay ninguna.</p>
              ) : (
                <ul className="cp-list">
                  {pending.map((p) => (
                    <li key={p.eventId} className="cp-row cp-loy-pending">
                      <span className="cp-avatar">{initials(p.nombre)}</span>
                      <span className="cp-row-main">
                        <span className="cp-row-title">{p.nombre}</span>
                        <span className="cp-row-sub">{p.servicio} · {relDay(p.start)} {fmtTime(p.start)}{p.staffName ? ` · ${p.staffName}` : ""}{p.visits != null ? ` · lleva ${p.visits}` : " · sin tarjeta"}</span>
                      </span>
                      <button type="button" className="cp-btn cp-btn-primary cp-loy-mini" disabled={busy === `pending:${p.eventId}`} onClick={() => registerPending(p)}>
                        {busy === `pending:${p.eventId}` ? <span className="cp-spin" /> : <Check size={16} />} Asistió
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          )}
        </div>

        <div className="cp-loy-col">
          {/* Lista de tarjetas */}
          {program && (
            <article className="cp-card cp-loy-cards" data-tour="lealtad.cards">
              <div className="cp-card-head">
                <h3 className="cp-card-title"><Sparkles size={18} /> Clientes</h3>
              </div>
              <div className="cp-input-wrap cp-loy-search">
                <Search size={16} />
                <input type="search" className="cp-input" placeholder="Buscar por nombre o teléfono" value={q} onChange={(e) => setQ(e.target.value)} />
                {q && <button type="button" className="cp-input-clear" onClick={() => setQ("")} aria-label="Limpiar"><X size={16} /></button>}
              </div>
              {loading ? (
                <div className="cp-stack"><div className="cp-skeleton" style={{ height: 64 }} /><div className="cp-skeleton" style={{ height: 64 }} /><div className="cp-skeleton" style={{ height: 64 }} /></div>
              ) : filtered.length === 0 ? (
                <div className="cp-empty">
                  <Gift size={32} />
                  <h3>{q ? "Nadie coincide" : "Aún no hay tarjetas"}</h3>
                  <p>{q ? "Prueba con otro nombre o los últimos dígitos del teléfono." : enabled ? "Se crean solas con la primera cita de cada cliente, o crea una con el botón de arriba." : "Activa el programa para empezar a sumar visitas."}</p>
                </div>
              ) : (
                <ul className="cp-list">
                  {filtered.map((c) => (
                    <li key={c.id}>
                      <button type="button" className="cp-row cp-loy-row" onClick={() => setSelected(c)}>
                        <span className="cp-avatar">{initials(c.name || c.phoneMasked)}</span>
                        <span className="cp-row-main">
                          <span className="cp-row-title">{c.name || c.phoneMasked}</span>
                          <span className="cp-row-sub">{c.phoneMasked}{c.lastVisitAt ? ` · última visita ${relDay(c.lastVisitAt).toLowerCase()}` : " · sin visitas"}</span>
                          <Progress visits={c.visits} max={c.maxVisits} completed={c.completed} />
                        </span>
                        <span className="cp-row-end">
                          {c.pendingRewards?.length > 0 && <span className="cp-pill cp-pill-warn"><Gift size={11} /> {c.pendingRewards.length}</span>}
                          {c.inWallet && <span className="cp-pill cp-pill-accent"><Wallet size={11} /></span>}
                          <ChevronRight size={18} className="cp-chev" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          )}
        </div>
      </div>

      {/* Sheet: detalle de tarjeta */}
      <Sheet open={!!selected} onClose={() => { setSelected(null); setAdjusting(false); }} side={side} title={selected?.name || selected?.phoneMasked || "Tarjeta"} subtitle={selected ? `${selected.phoneMasked}${selected.inWallet ? " · en Google Wallet" : ""}` : ""} className="cp-vars"
        footer={selected && !selected.completed && (
          <div className="cp-sheet-actions">
            <button type="button" className="cp-btn" disabled={busy === `send:${selected.id}`} onClick={() => send(selected)}>{busy === `send:${selected.id}` ? <span className="cp-spin" /> : <Send size={16} />} Reenviar</button>
            <button type="button" className="cp-btn cp-btn-primary" disabled={busy === `visit:${selected.id}`} onClick={() => registerVisit(selected)}>{busy === `visit:${selected.id}` ? <span className="cp-spin" /> : <Check size={16} />} Registrar visita</button>
          </div>
        )}
      >
        {selected && (
          <div className="cp-loy-detail">
            <Progress visits={selected.visits} max={selected.maxVisits} completed={selected.completed} />
            {selected.completed ? (
              <div className="cp-note cp-note-accent"><Sparkles size={16} /><div><b>Completó el programa.</b> Ya ganó todas las recompensas.</div></div>
            ) : selected.nextTier ? (
              <p className="cp-muted">Le faltan <b>{selected.nextTier.visits - selected.visits}</b> visitas para <b>{selected.nextTier.title}</b>.</p>
            ) : null}

            {selected.pendingRewards?.length > 0 && (
              <div className="cp-loy-rewards">
                <span className="cp-label">Por canjear</span>
                {selected.pendingRewards.map((r) => (
                  <div key={r.id} className="cp-loy-reward">
                    <Gift size={18} />
                    <span className="cp-row-main"><span className="cp-row-title">{r.title}</span><span className="cp-row-sub">Ganada el {fmtDate(r.earnedAt)}</span></span>
                    <button type="button" className="cp-btn cp-loy-mini" disabled={busy === `redeem:${r.id}`} onClick={() => redeem(selected, r)}>{busy === `redeem:${r.id}` ? <span className="cp-spin" /> : <Check size={15} />} Canjear</button>
                  </div>
                ))}
              </div>
            )}

            <dl className="cp-kv">
              <dt>Teléfono</dt><dd>{selected.phone}</dd>
              <dt>Visitas totales</dt><dd>{selected.totalVisits}{selected.cycle > 1 ? ` · ciclo ${selected.cycle}` : ""}</dd>
              <dt>Última visita</dt><dd>{selected.lastVisitAt ? `${relDay(selected.lastVisitAt)} ${fmtTime(selected.lastVisitAt)}` : "Sin visitas"}</dd>
              <dt>Tarjeta enviada</dt><dd>{selected.linkSentAt ? fmtDate(selected.linkSentAt) : "Todavía no"}</dd>
            </dl>

            {selected.redeemedRewards?.length > 0 && (
              <div>
                <span className="cp-label">Canjeadas</span>
                <ul className="cp-loy-history">
                  {selected.redeemedRewards.map((r) => <li key={r.id}>{r.title} <span className="cp-muted">· {fmtDate(r.redeemedAt)}</span></li>)}
                </ul>
              </div>
            )}

            <div className="cp-loy-links">
              <button type="button" className="cp-linkbtn" onClick={() => copyLink(selected)}><Link2 size={15} /> Copiar link</button>
              <button type="button" className="cp-linkbtn" onClick={() => setAdjusting((v) => !v)}><Pencil size={15} /> Corregir visitas</button>
              <button type="button" className="cp-linkbtn" onClick={() => rotate(selected)}><RefreshCw size={15} /> Link nuevo</button>
            </div>
            {adjusting && <AdjustVisits card={selected} busy={busy === `adjust:${selected.id}`} onSave={(v) => adjust(selected, v)} onCancel={() => setAdjusting(false)} />}
          </div>
        )}
      </Sheet>

      {/* Sheet: programa */}
      {program && (
        <ProgramSheet open={editingProgram} side={side} program={program} businessName={businessName} clientId={clientId}
          onClose={() => setEditingProgram(false)}
          onSaved={(loyalty) => { setEditingProgram(false); setSummary((s) => ({ ...s, program: { ...loyalty, maxVisits: loyalty.tiers?.length ? loyalty.tiers[loyalty.tiers.length - 1].visits : 0, enabled: !!loyalty.enabled && (loyalty.tiers?.length || 0) > 0 } })); refetchClientData?.(); load(); }}
        />
      )}

      {/* Sheet: nueva tarjeta */}
      <NewCardSheet open={showNew} side={side} clientId={clientId} onClose={() => setShowNew(false)} onCreated={(card) => { setShowNew(false); upsertCard(card); refreshSummary(); }} />
    </section>
  );
}

function AdjustVisits({ card, busy, onSave, onCancel }) {
  const [value, setValue] = useState(card.visits);
  return (
    <div className="cp-loy-adjust">
      <label className="cp-field">
        <span>Visitas en este ciclo <small>(máx. {card.maxVisits || "—"})</small></span>
        <input type="number" className="cp-input" min={0} max={card.maxVisits || 999} inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      <div className="cp-sheet-actions">
        <button type="button" className="cp-btn" onClick={onCancel}>Cancelar</button>
        <button type="button" className="cp-btn cp-btn-primary" disabled={busy} onClick={() => onSave(Math.max(0, parseInt(value, 10) || 0))}>{busy ? <span className="cp-spin" /> : <Save size={16} />} Guardar</button>
      </div>
    </div>
  );
}

const GOAL_PRESETS = [5, 8, 10, 12, 15];

/** Configuración del programa: recompensas por meta, ciclo y envío automático. */
function ProgramSheet({ open, side, program, businessName, clientId, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(!!program.enabled);
  const [tiers, setTiers] = useState(program.tiers.length ? program.tiers : [{ visits: 10, title: "" }]);
  const [repeatCycle, setRepeatCycle] = useState(program.repeatCycle !== false);
  const [sendOnFirst, setSendOnFirst] = useState(program.sendOnFirstAppointment !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEnabled(!!program.enabled);
    setTiers(program.tiers.length ? program.tiers.map((t) => ({ ...t })) : [{ visits: 10, title: "" }]);
    setRepeatCycle(program.repeatCycle !== false);
    setSendOnFirst(program.sendOnFirstAppointment !== false);
  }, [open, program]);

  const setTier = (i, patch) => setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeTier = (i) => setTiers((prev) => prev.filter((_, idx) => idx !== i));
  const addTier = () => setTiers((prev) => [...prev, { visits: (Math.max(0, ...prev.map((t) => Number(t.visits) || 0)) || 5) + 5, title: "" }]);

  const cleanTiers = tiers.map((t) => ({ visits: parseInt(t.visits, 10) || 0, title: (t.title || "").trim(), description: (t.description || "").trim() || undefined })).filter((t) => t.visits > 0 && t.title);
  const sorted = [...cleanTiers].sort((a, b) => a.visits - b.visits);
  const dupes = new Set(cleanTiers.map((t) => t.visits)).size !== cleanTiers.length;
  const canSave = !saving && (!enabled || (sorted.length > 0 && !dupes));

  const save = async () => {
    if (dupes) { showError("Dos recompensas tienen la misma meta", "Cada meta de visitas debe ser distinta."); return; }
    if (enabled && sorted.length === 0) { showError("Falta una recompensa", "Escribe al menos una recompensa con su meta para activar el programa."); return; }
    setSaving(true);
    try {
      const res = await LoyaltyService.updateSettings(clientId, { enabled, tiers: sorted, repeatCycle, sendOnFirstAppointment: sendOnFirst });
      showSuccess("Programa guardado", enabled ? "Tus clientes ya suman visitas." : "El programa queda apagado.");
      onSaved(res.loyalty || { enabled, tiers: sorted, repeatCycle, sendOnFirstAppointment: sendOnFirst });
    } catch (e) { notifyApiError(e, "Guardar programa"); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} side={side} title="Programa de lealtad" subtitle="Metas, recompensas y ciclo" className="cp-vars"
      footer={<div className="cp-sheet-actions"><button type="button" className="cp-btn" onClick={onClose}>Cancelar</button><button type="button" className="cp-btn cp-btn-primary" disabled={!canSave} onClick={save}>{saving ? <span className="cp-spin" /> : <Save size={16} />} Guardar</button></div>}
    >
      <div className="cp-form cp-loy-form">
        <div className="cp-switch">
          <div><div className="cp-switch-label">Programa activo</div><div className="cp-switch-hint">Apagado: nadie suma visitas y las tarjetas se conservan.</div></div>
          <button type="button" role="switch" aria-checked={enabled} className="cp-toggle" onClick={() => setEnabled((v) => !v)} aria-label="Programa activo" />
        </div>

        <div className="cp-field">
          <span>Recompensas por visitas</span>
          <div className="cp-loy-tier-editor">
            {tiers.map((t, i) => (
              <div key={i} className="cp-loy-tier-edit">
                <label className="cp-loy-tier-visits">
                  <input type="number" className="cp-input" min={1} max={200} inputMode="numeric" value={t.visits} onChange={(e) => setTier(i, { visits: e.target.value })} aria-label="Visitas" />
                  <small>visitas</small>
                </label>
                <input type="text" className="cp-input" placeholder="Recompensa (p. ej. Corte gratis)" value={t.title || ""} onChange={(e) => setTier(i, { title: e.target.value })} aria-label="Recompensa" />
                <button type="button" className="cp-iconbtn" onClick={() => removeTier(i)} aria-label="Quitar" disabled={tiers.length === 1}><Trash2 size={16} /></button>
              </div>
            ))}
            <div className="cp-loy-tier-tools">
              <button type="button" className="cp-linkbtn" onClick={addTier}><Plus size={15} /> Otra recompensa</button>
              <span className="cp-hint">Metas sugeridas: {GOAL_PRESETS.map((g, i) => <button key={g} type="button" className="cp-loy-preset" onClick={() => setTiers((prev) => prev.map((t, idx) => (idx === prev.length - 1 ? { ...t, visits: g } : t)))}>{g}</button>)}</span>
            </div>
          </div>
          {dupes && <small className="cp-loy-error">Hay dos recompensas con la misma meta.</small>}
        </div>

        <div className="cp-field">
          <span>Al completar la última meta</span>
          <div className="cp-segment cp-segment-fill">
            <button type="button" className={`cp-segment-btn ${repeatCycle ? "is-active" : ""}`} onClick={() => setRepeatCycle(true)}>Vuelve a empezar</button>
            <button type="button" className={`cp-segment-btn ${!repeatCycle ? "is-active" : ""}`} onClick={() => setRepeatCycle(false)}>Termina</button>
          </div>
          <small>{repeatCycle ? "La cuenta regresa a cero y las recompensas se pueden volver a ganar." : "Cada cliente gana cada recompensa una sola vez."}</small>
        </div>

        <div className="cp-switch">
          <div><div className="cp-switch-label">Mandar la tarjeta con la primera cita</div><div className="cp-switch-hint">El WhatsApp de confirmación lleva el link de su tarjeta.</div></div>
          <button type="button" role="switch" aria-checked={sendOnFirst} className="cp-toggle" onClick={() => setSendOnFirst((v) => !v)} aria-label="Mandar la tarjeta con la primera cita" />
        </div>

        <div className="cp-note">
          <Gift size={16} />
          <div>Si cambias el programa, las recompensas ya ganadas se conservan y las visitas acumuladas se evalúan con las metas nuevas en la siguiente visita.</div>
        </div>

        <div className="cp-field">
          <span>Así la ven tus clientes</span>
          <CardPreview businessName={businessName} tiers={sorted} repeatCycle={repeatCycle} />
        </div>
      </div>
    </Sheet>
  );
}

/** Alta manual: cliente sin cita (p. ej. que llegó de paso). */
function NewCardSheet({ open, side, clientId, onClose, onCreated }) {
  const [country, setCountry] = useState("MX");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [name, setName] = useState("");
  const [send, setSend] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setPhone(""); setName(""); setPhoneError(""); setSend(true); } }, [open]);

  const submit = async () => {
    if (!phone.trim()) return;
    if (phoneError) { showError("Número inválido", phoneError); return; }
    setSaving(true);
    try {
      const card = await LoyaltyService.createCard(clientId, { phone: normalizePhone(phone.trim(), country), country, name: name.trim(), send });
      showSuccess("Tarjeta creada", send ? "Le llegó el link por WhatsApp." : "");
      onCreated(card);
    } catch (e) { notifyApiError(e, "Crear tarjeta"); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} side={side} title="Nueva tarjeta" subtitle="Para clientes que llegan sin cita" className="cp-vars"
      footer={<div className="cp-sheet-actions"><button type="button" className="cp-btn" onClick={onClose}>Cancelar</button><button type="button" className="cp-btn cp-btn-primary" disabled={saving || !phone.trim() || !!phoneError} onClick={submit}>{saving ? <span className="cp-spin" /> : <Plus size={16} />} Crear</button></div>}
    >
      <div className="cp-form">
        <label className="cp-field">
          <span>Nombre</span>
          <input type="text" className="cp-input" placeholder="Como lo conoces" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="cp-field">
          <span>WhatsApp</span>
          <CountryPhoneSelector selectedCountry={country} onCountryChange={setCountry} phoneValue={phone} onPhoneChange={setPhone} phoneError={phoneError} setPhoneError={setPhoneError} placeholder="Número" />
        </div>
        <div className="cp-switch">
          <div><div className="cp-switch-label">Mandarle el link ahora</div><div className="cp-switch-hint">Por WhatsApp, con su tarjeta y cómo funciona.</div></div>
          <button type="button" role="switch" aria-checked={send} className="cp-toggle" onClick={() => setSend((v) => !v)} aria-label="Mandarle el link ahora" />
        </div>
      </div>
    </Sheet>
  );
}
